import { REFRESH_TIERS, type RefreshTier } from './types';

type TaskCallback = () => void | Promise<void>;

type RegisteredTask = {
    id: string;
    tier: RefreshTier;
    intervalMs: number;
    callback: TaskCallback;
    timer: ReturnType<typeof setInterval> | null;
    running: boolean;
    lastRunAt: number | null;
};

function boundedInterval(tier: RefreshTier, requestedMs?: number): number {
    const cfg = REFRESH_TIERS[tier];
    if (!requestedMs) return cfg.defaultMs;
    return Math.min(cfg.maxMs, Math.max(cfg.minMs, requestedMs));
}

export class ChartRefreshScheduler {
    private tasks = new Map<string, RegisteredTask>();

    register(id: string, tier: RefreshTier, callback: TaskCallback, intervalMs?: number): () => void {
        this.unregister(id);

        const task: RegisteredTask = {
            id,
            tier,
            intervalMs: boundedInterval(tier, intervalMs),
            callback,
            timer: null,
            running: false,
            lastRunAt: null,
        };

        this.tasks.set(id, task);
        this.startTask(task);

        return () => this.unregister(id);
    }

    unregister(id: string): void {
        const task = this.tasks.get(id);
        if (!task) return;
        if (task.timer) clearInterval(task.timer);
        this.tasks.delete(id);
    }

    clearAll(): void {
        for (const task of this.tasks.values()) {
            if (task.timer) clearInterval(task.timer);
        }
        this.tasks.clear();
    }

    snapshot(): Array<{ id: string; tier: RefreshTier; intervalMs: number; lastRunAt: number | null }> {
        return Array.from(this.tasks.values()).map(task => ({
            id: task.id,
            tier: task.tier,
            intervalMs: task.intervalMs,
            lastRunAt: task.lastRunAt,
        }));
    }

    private startTask(task: RegisteredTask): void {
        const run = async () => {
            if (task.running) return;
            task.running = true;
            try {
                await task.callback();
                task.lastRunAt = Date.now();
            } finally {
                task.running = false;
            }
        };

        void run();
        task.timer = setInterval(() => {
            void run();
        }, task.intervalMs);
    }
}

export const chartRefreshScheduler = new ChartRefreshScheduler();
